import {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  supabase,
} from "../supabaseClient";

import {
  API_BASE_URL,
} from "../config/apiBase";


// ============================================================
// INSTRUCTIONS PAGE
// ============================================================

export default function InstructionsPage({
  onContinue,
}) {
  const navigate =
    useNavigate();

  const [
    checking,
    setChecking,
  ] =
    useState(true);


  // ==========================================================
  // NORMALIZE PLAN
  // ==========================================================

  const normalizePlan =
    (p) => {
      const v =
        (p || "")
          .toString()
          .trim()
          .toLowerCase();

      if (!v) {
        return "";
      }

      if (
        v === "free_calculator" ||
        v.includes("calculator") ||
        v.includes("free")
      ) {
        return "free_calculator";
      }

      if (
        v.includes("deep")
      ) {
        return "deep_audit";
      }

      if (
        v.includes("snap")
      ) {
        return "snapshot";
      }

      return "";
    };


  // ==========================================================
  // REDIRECT FREE CALCULATOR USERS
  // ==========================================================

  useEffect(() => {
    const redirectCalculatorUsers =
      async () => {
        try {
          const localPlan =
            normalizePlan(
              localStorage.getItem(
                "selectedPlan"
              ) ||
              sessionStorage.getItem(
                "user_plan"
              )
            );


          if (
            localPlan ===
            "free_calculator"
          ) {
            navigate(
              "/calculator-instructions",
              {
                replace: true,
              }
            );

            return;
          }


          const {
            data,
          } =
            await supabase.auth
              .getSession();


          const authUid =
            data?.session
              ?.user
              ?.id;


          if (
            !authUid
          ) {
            setChecking(false);

            return;
          }


          const {
            data:
              dbUser,

            error,
          } =
            await supabase
              .from("users")
              .select(
                "plan, source, is_premium"
              )
              .eq(
                "auth_uid",
                authUid
              )
              .maybeSingle();


          if (
            error
          ) {
            console.log(
              "Plan lookup error:",
              error
            );

            setChecking(false);

            return;
          }


          const dbPlan =
            normalizePlan(
              dbUser?.plan
            );


          const dbSource =
            (
              dbUser?.source ||
              ""
            ).toLowerCase();


          console.log(
            "Instruction redirect check:",
            {
              authUid,
              dbUser,
              dbPlan,
              dbSource,
            }
          );


          if (
            dbPlan ===
              "free_calculator" ||
            dbSource ===
              "financial_calculator"
          ) {
            localStorage.setItem(
              "selectedPlan",
              "free_calculator"
            );


            sessionStorage.setItem(
              "user_plan",
              "free_calculator"
            );


            navigate(
              "/calculator-instructions",
              {
                replace: true,
              }
            );

            return;
          }
        }
        catch (
          err
        ) {
          console.log(
            "Instruction redirect failed:",
            err
          );
        }


        setChecking(false);
      };


    redirectCalculatorUsers();
  }, [
    navigate,
  ]);


  // ==========================================================
  // PAGE LOCK
  // ==========================================================

  useEffect(() => {
    document.body.classList.add(
      "app-lock"
    );


    return () => {
      document.body.classList.remove(
        "app-lock"
      );
    };
  }, []);


  // ==========================================================
  // CONTINUE
  // ==========================================================

  const handleContinue =
    async () => {
      try {
        const {
          data,
        } =
          await supabase.auth
            .getSession();


        const authUid =
          data?.session
            ?.user
            ?.id;


        if (
          authUid
        ) {
          const url =
            `${API_BASE_URL}/api/users/instructions-complete`;


          console.log(
            "[Instructions] Complete API:",
            url
          );


          await fetch(
            url,
            {
              method:
                "POST",

              headers: {
                "Content-Type":
                  "application/json",
              },

              body:
                JSON.stringify({
                  authUid,
                }),
            }
          );
        }


        localStorage.removeItem(
          "onboardingLock"
        );


        localStorage.removeItem(
          "justRegistered"
        );


        localStorage.setItem(
          "onboardingDone",
          "1"
        );


        localStorage.setItem(
          "seen_instructions",
          "1"
        );


        if (
          typeof onContinue ===
          "function"
        ) {
          onContinue();
        }


        navigate(
          "/dashboard",
          {
            replace: true,
          }
        );
      }
      catch (
        error
      ) {
        console.error(
          "[Instructions] Unable to save instructions completion:",
          error
        );


        // Do not trap the user on this page if the
        // completion API is temporarily unavailable.

        localStorage.removeItem(
          "onboardingLock"
        );


        localStorage.removeItem(
          "justRegistered"
        );


        localStorage.setItem(
          "onboardingDone",
          "1"
        );


        localStorage.setItem(
          "seen_instructions",
          "1"
        );


        if (
          typeof onContinue ===
          "function"
        ) {
          onContinue();
        }


        navigate(
          "/dashboard",
          {
            replace: true,
          }
        );
      }
    };


  // ==========================================================
  // CHECKING SCREEN
  // ==========================================================

  if (
    checking
  ) {
    return (
      <div
        className="
          min-h-screen
          bg-[#020617]
          text-white
          flex
          items-center
          justify-center
        "
      >
        Checking your registration type...
      </div>
    );
  }


  // ==========================================================
  // PAGE UI
  // ==========================================================

  return (
    <div
      className="
        app-shell
        bg-[#020617]
        text-slate-200
        font-['Inter']
      "
    >
      <header
        className="
          app-header
          bg-[#020617]/90
          backdrop-blur
          border-b
          border-white/10
          px-4
          sm:px-6
          py-4
        "
      >
        <div
          className="
            max-w-3xl
            mx-auto
            flex
            items-center
            justify-between
            gap-3
          "
        >
          <div>
            <h1
              className="
                text-lg
                sm:text-xl
                font-bold
                text-white
                leading-tight
              "
            >
              Guidelines for Your{" "}
              <span
                className="
                  text-[#eb6fbd]
                "
              >
                Career Strategy Session
              </span>
            </h1>


            <div
              className="
                mt-2
                h-1
                w-24
                bg-[#eb6fbd]
                rounded
              "
            />
          </div>


          <button
            type="button"

            onClick={() =>
              navigate(
                "/dashboard"
              )
            }

            className="
              shrink-0
              px-4
              py-2
              rounded-full
              bg-white/5
              border
              border-white/10
              text-white/90
              hover:bg-white/10
              transition
              text-sm
            "
          >
            Back
          </button>
        </div>
      </header>


      <main
        className="
          app-body
          px-4
          sm:px-6
          py-6
        "
      >
        <div
          className="
            max-w-3xl
            mx-auto
            space-y-6
          "
        >
          <section
            className="
              space-y-6
              text-slate-300
              leading-relaxed
            "
          >
            <Guideline
              number="1"
              title="Prepare Your Environment"
              text="Set aside 15–20 minutes in a quiet space. Focus directly impacts the quality of your career strategy."
            />


            <Guideline
              number="2"
              title="Be Honest About Your Timeline"
              text="Your timeline determines whether we prioritize fast-entry jobs or long-term upskilling."
            />


            <Guideline
              number="3"
              title="Complete the Psychometric Profile"
              text="Finish the O*NET Interest Profiler. Click on the link to the O*NET Interest Profiler, complete the 3 minute questionnaire and note your top two letters from the graph. Return to the platform and input the letters in the field provided."
            />


            <Guideline
              number="4"
              title="Define Your Preferred Environment"
              text="Choose where you thrive day-to-day, not what sounds impressive."
            />


            <Guideline
              number="5"
              title="Inventory Your Real Skills"
              text="List the skills you confidently use today. Transferable skills matter most."
            />


            <Guideline
              number="6"
              title="Be Geographic Specific"
              text="Provide a specific city and country, for example Austin, USA or Mumbai, India, for accurate market analysis."
            />


            <Guideline
              number="7"
              title="Payment & Processing"
              text="You’ll finalize payment only after completing your assessment."
            />
          </section>


          <div
            className="
              pt-4
              pb-8
              text-center
            "
          >
            <button
              type="button"

              onClick={
                handleContinue
              }

              className="
                px-10
                py-3
                rounded-full
                font-bold
                bg-gradient-to-r
                from-[#eb6fbd]
                to-[#d946ef]
                text-[#0b0b16]
                shadow-lg
                shadow-[#eb6fbd]/20
                hover:scale-105
                transition
              "
            >
              Continue to Dashboard →
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}


// ============================================================
// GUIDELINE CARD
// ============================================================

function Guideline({
  number,
  title,
  text,
}) {
  return (
    <div
      className="
        rounded-2xl
        border
        border-white/10
        bg-white/5
        p-5
        shadow-lg
        shadow-black/20
      "
    >
      <h3
        className="
          text-lg
          sm:text-xl
          font-bold
          text-white
          mb-2
        "
      >
        <span
          className="
            text-[#eb6fbd]
            mr-2
          "
        >
          {number}.
        </span>

        {title}
      </h3>


      <p
        className="
          text-slate-300
        "
      >
        {text}
      </p>
    </div>
  );
}